import { t } from 'i18next'
import { z } from 'zod'

import appDefaults from '~/portal/portal.defaults.json'
import {
  domainRegex,
  productNameRegex,
  slugRegex,
  subdomainRegex,
  urlRegex
} from '~/portal/utils/shared/regular.expressions.ts'
import { WebhookUrlOptions, WebhookUrlProblem, getWebhookUrlProblem } from '~/shared/utils/webhookUrl'

export const subdomainSchema = () => z.string()
  .min(appDefaults.portalSettings.portalSubdomainMinLength, t('errors.components.ui.forms.input.text.generalMinLength', { minLength: appDefaults.portalSettings.portalSubdomainMinLength }))
  .max(appDefaults.portalSettings.portalSubdomainMaxLength, t('errors.components.ui.forms.input.text.generalMaxLength', { maxLength: appDefaults.portalSettings.portalSubdomainMaxLength }))
  .regex(subdomainRegex, t('errors.components.ui.forms.input.text.nameRestriction'))

export const slugSchema = () => z.string()
  .min(appDefaults.productsSettings.productSlugMinLength, t('errors.components.ui.forms.input.text.generalMinLength', { minLength: appDefaults.productsSettings.productSlugMinLength }))
  .max(appDefaults.productsSettings.productSlugMaxLength, t('errors.components.ui.forms.input.text.generalMaxLength', { maxLength: appDefaults.productsSettings.productSlugMaxLength }))
  .regex(slugRegex, t('errors.components.ui.forms.input.text.nameRestriction')).pipe(
    z.string().refine(value => !appDefaults.productsSettings.reservedSlugs.includes(value), t('errors.components.ui.forms.input.text.reservedName')),
  )

export const contentSlugSchema = () => z.string()
  .min(appDefaults.tableOfContents.pageSlugMinLength, { message: t('errors.components.ui.forms.input.text.slugMinLength') })
  .max(appDefaults.tableOfContents.pageSlugMaxLength, { message: t('errors.components.ui.forms.input.text.slugMaxLength') })
  .regex(slugRegex, t('errors.components.ui.forms.input.text.nameRestriction'))

export const productNameSchema = () => z.string().transform(value => value.trim()).pipe(
  z.string()
    .min(appDefaults.productsSettings.productNameMinLength, t('errors.components.ui.forms.input.text.generalMinLength', { minLength: appDefaults.productsSettings.productNameMinLength }))
    .max(appDefaults.productsSettings.productNameMaxLength, t('errors.components.ui.forms.input.text.generalMaxLength', { maxLength: appDefaults.productsSettings.productNameMaxLength }))
    .regex(productNameRegex, t('errors.components.ui.forms.input.text.productNameRestriction'))
)

export const domainSchema = () => z.string().regex(domainRegex, t('errors.components.ui.forms.input.text.invalidUrl'))

export const urlSchema = (message = t('errors.components.ui.forms.input.text.invalidUrl')) => z.string().regex(urlRegex, message)

// Matches the backend webhookUrl limit; a longer URL would pass here and fail the Apply PUT
export const WEBHOOK_URL_MAX_LENGTH = 500
const TOKEN_ORIGIN_MAX_LENGTH = 2048
export const TOKEN_ORIGIN_MAX_COUNT = 10

// The structural rules live in one shared module that the server guard (safeUrl) also uses
const webhookUrlProblem = (value: string, options?: WebhookUrlOptions): WebhookUrlProblem | null => {
  const result = getWebhookUrlProblem(value, options)
  return 'problem' in result ? result.problem : null
}

export const webhookUrlSchema = (options?: WebhookUrlOptions) => z.string().trim()
  .max(WEBHOOK_URL_MAX_LENGTH, t('errors.components.ui.forms.input.text.generalMaxLength', { maxLength: WEBHOOK_URL_MAX_LENGTH }))
  .refine(value => URL.canParse(value), t('errors.components.ui.forms.input.text.invalidUrl'))
  .refine(value => webhookUrlProblem(value, options) !== 'https required', t('errors.components.ui.forms.input.text.httpsRequired'))
  .refine(value => webhookUrlProblem(value, options) !== 'credentials not allowed', t('errors.components.ui.forms.input.text.webhookCredentialsNotAllowed'))
  .refine(value => webhookUrlProblem(value, options) !== 'port not allowed', t('errors.components.ui.forms.input.text.webhookPortNotAllowed'))

const toOrigin = (value: string, { allowInsecure = false }: WebhookUrlOptions = {}): string | null => {
  if (!URL.canParse(value) || /[?#]/.test(value)) { return null }
  const url = new URL(value)
  const isOriginOnly = (url.protocol === 'https:' || (allowInsecure && url.protocol === 'http:')) && url.pathname === '/' && !url.username && !url.password
  return isOriginOnly ? url.origin : null
}

export const tokenOriginSchema = (options?: WebhookUrlOptions) => z.string().trim()
  .max(TOKEN_ORIGIN_MAX_LENGTH, t('errors.components.ui.forms.input.text.generalMaxLength', { maxLength: TOKEN_ORIGIN_MAX_LENGTH }))
  .refine(value => toOrigin(value, options) !== null, t('errors.components.ui.forms.input.text.invalidOrigin'))
  .transform(value => toOrigin(value, options) as string)

export const tokenOriginsSchema = (options?: WebhookUrlOptions) => z.array(tokenOriginSchema(options))
  .max(TOKEN_ORIGIN_MAX_COUNT, t('errors.components.ui.forms.input.text.originMaxCount', { maxCount: TOKEN_ORIGIN_MAX_COUNT }))
